// src/components/Income/IncomeTable.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../../api';
import { AVAILABLE_YEARS } from '../../utils/incomeConstants';
import { calculateTaxableIncome, getAbatementDescription } from '../../utils/incomeCalculations';
import WizardAddIncome from './WizardAddIncome';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../../hooks/useConfirm';
import './IncomeTable.css';

// Configuration des types pour affichage
const INCOME_TYPE_CONFIG = {
  SALARIE: { label: 'Salarié', icon: '💼', abatementType: 'SALAIRE' },
  CHOMAGE: { label: 'Chômage', icon: '📋', abatementType: 'CHOMAGE' },
  LMNP: { label: 'LMNP', icon: '🏠', abatementType: (fields) => fields?.regime === 'REEL' ? 'LMNP_REEL' : 'LMNP_MICRO' },
  EI: { label: 'Entreprise individuelle', icon: '🏪', abatementType: (fields) => {
    if (fields?.regime === 'REEL') {
      return fields?.activityType === 'BNC' ? 'REEL_BNC' : 'REEL_BIC';
    }
    if (fields?.activityType === 'BNC') return 'MICRO_BNC';
    if (fields?.activityType === 'BIC_VENTE') return 'MICRO_BIC_VENTE';
    return 'MICRO_BIC_SERVICE';
  }},
  SOCIETE_IR: { label: 'Quote-part société IR', icon: '🏢', abatementType: 'SOCIETE_IR' }
};

const IncomeTable = ({ individualId, individualName, entities = [] }) => {
  const [incomes, setIncomes] = useState([]);
  const [selectedYear, setSelectedYear] = useState(AVAILABLE_YEARS[0]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);

  // Confirm modal state
  const { confirmState, showConfirm, hideConfirm, handleConfirm } = useConfirm();

  const loadIncomes = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiService.fetchIncomes(individualId);
      setIncomes(data);
    } catch (error) {
      console.error('Error loading incomes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [individualId]);

  useEffect(() => {
    loadIncomes();
  }, [loadIncomes]);

  // Filtrer les revenus actifs pour l'année sélectionnée
  const filteredIncomes = useMemo(() => {
    return incomes.filter(income => {
      const startDate = new Date(income.start_date || income.startDate);
      const endDate = income.end_date || income.endDate ? new Date(income.end_date || income.endDate) : null;

      const startYear = startDate.getFullYear();
      const endYear = endDate ? endDate.getFullYear() : Infinity;

      return startYear <= selectedYear && selectedYear <= endYear;
    });
  }, [incomes, selectedYear]);

  // Obtenir le montant pour une année donnée
  const getYearlyAmount = (income, year) => {
    const yearlyAmounts = income.yearly_amounts || income.yearlyAmounts || [];
    const yearData = yearlyAmounts.find(ya => ya.year === year);
    return yearData?.amount || 0;
  };

  // Calculer le revenu imposable
  const calculateIncomeData = (income, year) => {
    const amount = getYearlyAmount(income, year);
    const incomeType = income.income_type || income.incomeType;
    const specificFields = income.specific_fields || income.specificFields || {};
    const config = INCOME_TYPE_CONFIG[incomeType];

    if (!config) return { gross: amount, taxable: amount, abatement: '-' };

    const abatementType = typeof config.abatementType === 'function'
      ? config.abatementType(specificFields)
      : config.abatementType;

    const mockIncome = {
      incomeType: abatementType,
      grossAmount: amount,
      expenses: specificFields.charges || 0
    };

    const taxable = calculateTaxableIncome(mockIncome, year);
    const abatement = getAbatementDescription(abatementType, year);

    return { gross: amount, taxable, abatement };
  };

  const handleAddIncome = async (incomeData) => {
    try {
      await apiService.createIncome(individualId, incomeData);
      await loadIncomes();
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding income:', error);
    }
  };

  const handleUpdateIncome = async (incomeData) => {
    try {
      await apiService.updateIncome(editingIncome.id, incomeData);
      await loadIncomes();
      setEditingIncome(null);
    } catch (error) {
      console.error('Error updating income:', error);
    }
  };

  const handleDeleteIncome = (incomeId, incomeType) => {
    const typeConfig = INCOME_TYPE_CONFIG[incomeType] || { label: 'revenu', icon: '📋' };
    showConfirm({
      title: 'Supprimer ce revenu ?',
      message: `Voulez-vous vraiment supprimer ce revenu de type "${typeConfig.label}" ?`,
      confirmText: 'Supprimer',
      type: 'danger',
      onConfirm: async () => {
        try {
          await apiService.deleteIncome(incomeId);
          await loadIncomes();
        } catch (error) {
          console.error('Error deleting income:', error);
        }
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { month: '2-digit', year: 'numeric' });
  };

  // Calculer les totaux
  const totals = useMemo(() => {
    return filteredIncomes.reduce((acc, income) => {
      const data = calculateIncomeData(income, selectedYear);
      return {
        gross: acc.gross + data.gross,
        taxable: acc.taxable + data.taxable
      };
    }, { gross: 0, taxable: 0 });
  }, [filteredIncomes, selectedYear]);

  // Compter les revenus par année pour les indicateurs
  const incomeCountByYear = useMemo(() => {
    const counts = {};
    AVAILABLE_YEARS.forEach(year => {
      counts[year] = incomes.filter(income => {
        const startDate = new Date(income.start_date || income.startDate);
        const endDate = income.end_date || income.endDate ? new Date(income.end_date || income.endDate) : null;
        const startYear = startDate.getFullYear();
        const endYear = endDate ? endDate.getFullYear() : Infinity;
        return startYear <= year && year <= endYear;
      }).length;
    });
    return counts;
  }, [incomes]);

  if (isLoading) {
    return <div className="income-loading">Chargement des revenus...</div>;
  }

  return (
    <div className="income-table-container">
      <div className="income-header">
        <h4>Revenus de {individualName}</h4>
        <button
          className="add-income-btn"
          onClick={() => setShowAddModal(true)}
        >
          + Ajouter un revenu
        </button>
      </div>

      {/* Onglets par année */}
      <div className="year-tabs">
        {AVAILABLE_YEARS.map(year => (
          <button
            key={year}
            className={`year-tab ${selectedYear === year ? 'active' : ''}`}
            onClick={() => setSelectedYear(year)}
          >
            {year}
            {incomeCountByYear[year] > 0 && (
              <span className="year-count">{incomeCountByYear[year]}</span>
            )}
          </button>
        ))}
      </div>

      {filteredIncomes.length === 0 ? (
        <div className="no-incomes">
          <span className="no-incomes-icon">📭</span>
          <span>Aucun revenu actif en {selectedYear}</span>
          <button className="add-link" onClick={() => setShowAddModal(true)}>
            + Ajouter un revenu
          </button>
        </div>
      ) : (
        <table className="income-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Période</th>
              <th className="amount-col">Brut {selectedYear}</th>
              <th>Abattement</th>
              <th className="amount-col">Imposable</th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredIncomes.map(income => {
              const incomeType = income.income_type || income.incomeType;
              const config = INCOME_TYPE_CONFIG[incomeType] || { label: incomeType, icon: '📋' };
              const data = calculateIncomeData(income, selectedYear);
              const startDate = income.start_date || income.startDate;
              const endDate = income.end_date || income.endDate;

              return (
                <tr key={income.id}>
                  <td className="type-cell">
                    <span className="type-icon">{config.icon}</span>
                    <span className="type-label">{config.label}</span>
                  </td>
                  <td className="period-cell">
                    <span className="period">
                      {formatDate(startDate)} → {endDate ? formatDate(endDate) : 'En cours'}
                    </span>
                  </td>
                  <td className="amount">{formatCurrency(data.gross)}</td>
                  <td className="abatement">{data.abatement}</td>
                  <td className="amount taxable">{formatCurrency(data.taxable)}</td>
                  <td className="actions">
                    <button
                      className="edit-btn"
                      onClick={() => setEditingIncome(income)}
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteIncome(income.id, income.income_type)}
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="totals-row">
              <td colSpan="2"><strong>TOTAL {selectedYear}</strong></td>
              <td className="amount"><strong>{formatCurrency(totals.gross)}</strong></td>
              <td></td>
              <td className="amount taxable"><strong>{formatCurrency(totals.taxable)}</strong></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      )}

      {showAddModal && (
        <WizardAddIncome
          entities={entities}
          onSave={handleAddIncome}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {editingIncome && (
        <WizardAddIncome
          income={editingIncome}
          entities={entities}
          onSave={handleUpdateIncome}
          onClose={() => setEditingIncome(null)}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        details={confirmState.details}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </div>
  );
};

export default IncomeTable;
